/**
 * Tiny IndexedDB-backed flag shared between the service worker and the page.
 *
 * When the browser rotates/invalidates a push subscription it fires
 * `pushsubscriptionchange` in the SW. The SW can re-subscribe at the browser
 * level, but registering the new subscription with our backend
 * (POST /api/push/subscriptions) requires the user's auth token, which only
 * the page has. The SW therefore sets this flag (and pings open clients);
 * the next authenticated boot re-registers and clears it.
 *
 * IndexedDB is the only storage both contexts share — the SW has no
 * localStorage.
 */

const DB_NAME = "slumbr-push";
const DB_VERSION = 1;
const STORE_NAME = "flags";
const FLAG_KEY = "needsResubscribe";

function openFlagDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME);
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

async function withFlagStore<T>(
  mode: IDBTransactionMode,
  run: (store: IDBObjectStore) => IDBRequest<T>
): Promise<T> {
  const db = await openFlagDb();
  try {
    return await new Promise<T>((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, mode);
      const request = run(tx.objectStore(STORE_NAME));
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  } finally {
    db.close();
  }
}

export async function setPushResubscribeFlag(): Promise<void> {
  await withFlagStore("readwrite", (store) => store.put(true, FLAG_KEY));
}

export async function getPushResubscribeFlag(): Promise<boolean> {
  const value = await withFlagStore("readonly", (store) => store.get(FLAG_KEY));
  return value === true;
}

export async function clearPushResubscribeFlag(): Promise<void> {
  await withFlagStore("readwrite", (store) => store.delete(FLAG_KEY));
}
